import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const PRODUCTION_IMAGE =
  "https://customer-assets.emergentagent.com/job_entertainment-hub-187/artifacts/k4qar6dq_file_00000000286471f487b3e5116bfb575c.png";
const CHARACTER_IMAGE =
  "https://customer-assets.emergentagent.com/job_entertainment-hub-187/artifacts/l2fkpgg2_file_00000000ea607243b559930d284c7789.png";
const CABARET_IMAGE =
  "https://customer-assets.emergentagent.com/job_entertainment-hub-187/artifacts/t0svsves_file_0000000050fc71f48fa2a9500f963f97.png";
const TEAM_TRAINING_IMAGE =
  "https://customer-assets.emergentagent.com/job_entertainment-hub-187/artifacts/2kdrs4zu_file_00000000ec6871f4a6e0397e6e3cd325.png";
const SCREEN_IMAGE =
  "https://customer-assets.emergentagent.com/job_entertainment-hub-187/artifacts/rvvf65bu_file_00000000c60071f4a9455417ce985769.png";
const INSTALLATIONS_IMAGE =
  "https://customer-assets.emergentagent.com/job_entertainment-hub-187/artifacts/no30z6q8_file_0000000028c471f48028ac3966363d23.png";
const PRINT_IMAGE =
  "https://customer-assets-lqy194kg.emergentagent.net/job_entertainment-hub-187/artifacts/ms4ueun8_WhatsApp%20Image%202026-07-22%20at%2019.34.34.jpeg";

const services = [
  {
    image: PRODUCTION_IMAGE,
    title: "Live Production Shows",
    copy: "High-quality live shows created for holiday centres, leisure venues and family audiences. From themed spectaculars and seasonal productions to touring shows and main stage entertainment, we create productions with strong visuals, professional performance, engaging storytelling and lasting audience impact.",
    cta: "Explore Production Shows",
  },
  {
    image: CHARACTER_IMAGE,
    title: "Character Creation & Brand Development",
    copy: "We create original entertainment characters from the ground up, including brand concepts, logo development, costume direction, visual style, animation, storytelling and full production integration. Whether you are launching a new attraction, building a family entertainment brand or refreshing your existing characters, we create identities that audiences connect with.",
    cta: "Create Your Character Brand",
  },
  {
    image: CABARET_IMAGE,
    title: "Cabaret Acts & Tribute Entertainment",
    copy: "Robust Entertainment can supply professional cabaret acts, including some of the UK's finest tribute acts, vocalists, speciality performers and headline entertainment. Our experience in holiday park and leisure entertainment means we understand how to programme acts that suit your audience, your venue and your commercial goals.",
    cta: "Ask About Cabaret Acts",
  },
  {
    image: SCREEN_IMAGE,
    title: "Screen Content, Motion Graphics & Promotional Media",
    copy: "We produce visual content for LED screens, advertising campaigns, event promotion and branded entertainment. This can include motion graphics, animation, show visuals, promotional videos, digital advertising assets and branded screen content.",
    cta: "Upgrade Your Visual Content",
  },
  {
    image: TEAM_TRAINING_IMAGE,
    title: "Entertainment Team Training",
    copy: "Exceptional entertainment starts with confident people. We train entertainment teams to improve performance, guest engagement, professionalism and delivery across daytime activities, family events, character experiences and large-scale stage productions.",
    cta: "Develop Your Team",
  },
  {
    image: INSTALLATIONS_IMAGE,
    title: "Technical Installations, Sound, Lighting & LED Screens",
    copy: "Robust Entertainment provides high-quality technical installations for venues looking to upgrade their entertainment spaces. Services include sound systems, lighting, LED screens, show technology and installation support, delivered by an efficient team using quality products. We also provide full backup and ongoing support, giving venues confidence long after installation is complete.",
    cta: "Discuss Your Installation",
  },
  {
    image: PRINT_IMAGE,
    title: "Design and Print Services",
    copy: "We provide complete design and print services, including brochures, leaflets, posters, signage, branded uniforms, embroidery and garment printing. From initial artwork to final production, we create professional, consistent materials that strengthen your brand and help your business stand out.",
    cta: "Start Your Design Project",
  },
];

export const Services = () => {
  return (
    <section id="services" className="relative py-14 lg:py-20 bg-[#0D0D0D] border-y border-[#D4AF37]/25 overflow-hidden">
      <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/5 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-sm sm:text-base font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-4 text-center">Services</p>
        <h2 className="font-heading heading-fluid font-medium tracking-normal text-metal max-w-3xl mx-auto text-center">
          A Complete Range of Entertainment Services
        </h2>
        <p className="mt-6 text-lg text-zinc-300 leading-relaxed max-w-3xl mx-auto text-center">
          Robust Entertainment gives venues access to a complete entertainment department without the need to manage
          multiple suppliers. Our services are designed to work together, giving you creative consistency, technical
          reliability and a stronger guest experience.
        </p>
        <div className="mt-10 grid md:grid-cols-2 gap-6" data-testid="services-grid">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.1 }}
              className={`group relative bg-[#121212] border border-white/10 rounded-2xl overflow-hidden hover:border-[#FF2A2A]/40 transition-colors duration-300 flex flex-col text-center ${
                services.length % 2 === 1 && i === services.length - 1
                  ? "md:col-span-2 md:w-[calc(50%-12px)] md:mx-auto"
                  : ""
              }`}
            >
              {s.image && (
                <div className="w-full aspect-[16/9] overflow-hidden border-b border-white/10">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    data-testid={`service-image-${i}`}
                  />
                </div>
              )}
              <div className="flex flex-col items-center p-8 lg:p-10 flex-1">
                <h3 className="font-heading text-xl sm:text-2xl font-medium text-white mb-5">{s.title}</h3>
                <p className="text-base text-zinc-300 leading-relaxed flex-1">{s.copy}</p>
                <a
                  href="https://wa.me/447539528505"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-cta-${i}`}
                  className="mt-6 inline-flex items-center gap-2 text-base font-semibold text-[#FF4D4D] hover:text-white transition-colors duration-200"
                >
                  {s.cta}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
